import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import { formatDate, formatHKD } from "./calculations";
import type { Invoice } from "./types";

const FONT_PATH = "/fonts/NotoSansTC-Variable.ttf";
const FONT_NAME = "NotoSansTC";
let fontBase64Promise: Promise<string> | null = null;

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 32_768;
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

async function getFontBase64(): Promise<string> {
  fontBase64Promise ??= fetch(FONT_PATH).then(async (response) => {
    if (!response.ok) throw new Error("Unable to load PDF search font");
    return arrayBufferToBase64(await response.arrayBuffer());
  });
  return fontBase64Promise;
}

function safeFilename(value: string): string {
  return (
    value
      .normalize("NFKD")
      .replace(/[^\p{L}\p{N}._-]+/gu, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 90) || "invoice"
  );
}

export function invoiceFilename(invoice: Invoice): string {
  return safeFilename(
    `invoice-${invoice.number}-${invoice.client.name || "client"}-${invoice.issueDate}`,
  );
}

function includeInExport(node: HTMLElement): boolean {
  return node.dataset.exportExclude !== "true";
}

function waitForPendingReceiptUrls(
  container: HTMLElement,
  timeoutMs: number,
): Promise<void> {
  if (!container.querySelector('[data-export-image-pending="true"]')) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      observer.disconnect();
      reject(new Error("Receipt images are still loading. Please try again."));
    }, timeoutMs);
    const observer = new MutationObserver(() => {
      if (!container.querySelector('[data-export-image-pending="true"]')) {
        window.clearTimeout(timeout);
        observer.disconnect();
        resolve();
      }
    });
    observer.observe(container, {
      attributes: true,
      childList: true,
      subtree: true,
    });
  });
}

async function decodeImage(image: HTMLImageElement): Promise<void> {
  if (!image.complete) {
    await new Promise<void>((resolve, reject) => {
      const loaded = () => {
        cleanup();
        resolve();
      };
      const failed = () => {
        cleanup();
        reject(new Error(`Unable to load image: ${image.alt || "invoice image"}`));
      };
      const cleanup = () => {
        image.removeEventListener("load", loaded);
        image.removeEventListener("error", failed);
      };
      image.addEventListener("load", loaded, { once: true });
      image.addEventListener("error", failed, { once: true });
    });
  }
  if (image.naturalWidth === 0) {
    throw new Error(`Unable to load image: ${image.alt || "invoice image"}`);
  }
  if (typeof image.decode === "function") {
    await image.decode();
  }
}

export async function waitForExportImages(
  container: HTMLElement,
  timeoutMs = 15_000,
): Promise<void> {
  await waitForPendingReceiptUrls(container, timeoutMs);
  const images = [...container.querySelectorAll<HTMLImageElement>("img")];
  await Promise.race([
    Promise.all(images.map(decodeImage)).then(() => undefined),
    new Promise<never>((_, reject) =>
      window.setTimeout(
        () => reject(new Error("Invoice images took too long to load.")),
        timeoutMs,
      ),
    ),
  ]);
}

async function renderInvoicePage(
  page: HTMLElement,
  pixelRatio: 1 | 2,
): Promise<string> {
  const previousTransform = page.style.transform;
  const previousMargin = page.style.marginBottom;
  const previousBoxShadow = page.style.boxShadow;
  page.style.transform = "none";
  page.style.marginBottom = "0";
  page.style.boxShadow = "none";
  try {
    return await toPng(page, {
      cacheBust: true,
      pixelRatio,
      backgroundColor: "#ffffff",
      filter: (node) =>
        !(node instanceof HTMLElement) || includeInExport(node),
    });
  } finally {
    page.style.transform = previousTransform;
    page.style.marginBottom = previousMargin;
    page.style.boxShadow = previousBoxShadow;
  }
}

function visiblePageText(page: HTMLElement): string[] {
  return page.innerText
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
}

export async function exportInvoicePdf(
  container: HTMLElement,
  invoice: Invoice,
): Promise<void> {
  await document.fonts.ready;
  await waitForExportImages(container);
  const pages = [...container.querySelectorAll<HTMLElement>(".invoice-paper")];
  if (pages.length === 0) throw new Error("No invoice pages to export");

  const format = invoice.paperSize === "letter" ? "letter" : "a4";
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format,
    putOnlyUsedFonts: true,
    compress: true,
  });
  doc.addFileToVFS("NotoSansTC.ttf", await getFontBase64());
  doc.addFont("NotoSansTC.ttf", FONT_NAME, "normal", 400);

  for (const [index, page] of pages.entries()) {
    if (index > 0) doc.addPage(format, "portrait");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageImage = await renderInvoicePage(page, 2);

    doc.addImage(
      pageImage,
      "PNG",
      0,
      0,
      pageWidth,
      pageHeight,
      undefined,
      "FAST",
    );

    // Preserve search/copy support without changing the screen-faithful visual.
    doc.setFont(FONT_NAME, "normal");
    doc.setFontSize(1);
    doc.text(visiblePageText(page), 2, 2, {
      renderingMode: "invisible",
      lineHeightFactor: 1.15,
    });
  }

  doc.save(`${invoiceFilename(invoice)}.pdf`);
}

export async function exportInvoicePngPages(
  container: HTMLElement,
  invoice: Invoice,
  scale: 1 | 2,
): Promise<void> {
  await document.fonts.ready;
  await waitForExportImages(container);
  const pages = [...container.querySelectorAll<HTMLElement>(".invoice-paper")];
  if (pages.length === 0) throw new Error("No invoice pages to export");
  for (const [index, page] of pages.entries()) {
    const dataUrl = await renderInvoicePage(page, scale);
    const link = document.createElement("a");
    link.download = `${invoiceFilename(invoice)}-page-${index + 1}@${scale}x.png`;
    link.href = dataUrl;
    link.click();
    await new Promise<void>((resolve) => window.setTimeout(resolve, 160));
  }
}

export function invoiceSummary(invoice: Invoice): string {
  return [
    `${invoice.number} · ${invoice.client.name || "Client"}`,
    `Issue: ${formatDate(invoice.issueDate)}`,
    `Due: ${formatDate(invoice.dueDate)}`,
    `Total: ${formatHKD(invoice.totalMinor)}`,
    `Paid: ${formatHKD(invoice.paidMinor)}`,
    `Balance: ${formatHKD(invoice.balanceMinor)}`,
  ].join("\n");
}
